/**
 * Variant Media Grouping.
 *
 * Lets a color variant show ALL of its images, not just the single featured
 * image Shopify allows per variant. Images are tagged in their alt text with
 * `#ColorName` (matching a color option value). Selecting a color shows every
 * image whose group matches, plus any untagged (shared) images.
 *
 * This filters media IN PLACE inside the existing gallery — it never rebuilds
 * the gallery, so the Swiper (main slider + thumbs) keeps its layout, direction
 * and heights across color switches.
 *
 * Public API: window.updateVariantMediaDisplay(sectionId, variant)
 *   - sectionId: the section id (as used in `data-section="product-media-<id>"`)
 *   - variant:   the selected variant object (may be null / have no featured_media)
 */
(function () {
  "use strict";

  var MAIN_HIDDEN_CLASS = "product__media-item--variant-hidden";
  var SUB_HIDDEN_CLASS = "product__media-subitem--variant-hidden";

  // Mirror Shopify's `| handle` filter so the JS group key matches the one the
  // Liquid produced for `data-media-group` (lowercase, diacritics stripped,
  // non-alphanumeric collapsed to single hyphens, trimmed).
  function handleize(value) {
    if (value == null) return "";
    return String(value)
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  // Empty group => shared image, always visible.
  // No selected group (no color / no variant) => show everything.
  function matchesGroup(el, selectedGroup) {
    var group = el.getAttribute("data-media-group") || "";
    if (group === "") return true;
    if (selectedGroup === "") return true;
    return group === selectedGroup;
  }

  function resolveScope(rootEl) {
    return (
      rootEl.closest(".product") ||
      rootEl.closest("quick-add-modal") ||
      rootEl.closest(".shopify-section") ||
      document
    );
  }

  function findRoot(sectionId, scope) {
    return (scope || document).querySelector(
      '[data-section="product-media-' +
        sectionId +
        '"][data-alt-media-grouping="true"]'
    );
  }

  // Toggle visibility + swiper-slide participation for a set of media items.
  function toggleItems(items, selectedGroup, hiddenClass) {
    items.forEach(function (item) {
      var show = matchesGroup(item, selectedGroup);
      item.classList.toggle(hiddenClass, !show);

      // Only elements that are direct children of a .swiper-wrapper are Swiper
      // slides. Hidden slides MUST drop `swiper-slide` or Swiper miscalculates.
      var parent = item.parentElement;
      if (parent && parent.classList.contains("swiper-wrapper")) {
        item.classList.toggle("swiper-slide", show);
      }
    });
  }

  // Refresh a Swiper after slides were shown/hidden, WITHOUT destroying it.
  // Destroying would reset direction / thumbs position / heights.
  function refreshSwiper(swiperEl) {
    var swiper = swiperEl && swiperEl.swiper;
    if (!swiper) return;

    var wasVertical =
      typeof swiper.isVertical === "function" ? swiper.isVertical() : false;

    try {
      swiper.updateSize();
      swiper.updateSlides();
      swiper.updateProgress();
      swiper.updateSlidesClasses();
      swiper.update();
    } catch (e) {
      /* noop */
    }

    // If update() re-evaluated breakpoints and flipped direction, restore it.
    var nowVertical =
      typeof swiper.isVertical === "function" ? swiper.isVertical() : false;
    if (
      wasVertical !== nowVertical &&
      typeof swiper.changeDirection === "function"
    ) {
      swiper.changeDirection(wasVertical ? "vertical" : "horizontal");
    }
  }

  // Slide the main gallery (and thumbs) to the selected variant's featured
  // media if it is visible, otherwise to the first visible slide.
  function slideToSelected(
    mainListEl,
    subListEl,
    variant,
    sectionId,
    selectedGroup
  ) {
    var mainSwiper = mainListEl && mainListEl.swiper;
    if (!mainSwiper) return;

    var target = null;

    if (
      variant &&
      variant.featured_media &&
      variant.featured_media.id != null
    ) {
      var featuredId = sectionId + "-" + variant.featured_media.id;
      var featuredSlide = mainListEl.querySelector(
        '.swiper-slide[data-media-id="' + featuredId + '"]'
      );
      if (
        featuredSlide &&
        !featuredSlide.classList.contains(MAIN_HIDDEN_CLASS)
      ) {
        target = featuredSlide;
      }
    }

    if (!target) {
      target = mainListEl.querySelector(
        ".swiper-slide[data-media-id]:not(." + MAIN_HIDDEN_CLASS + ")"
      );
    }
    if (!target) return;

    var index = mainSwiper.slides.indexOf
      ? mainSwiper.slides.indexOf(target)
      : Array.prototype.indexOf.call(mainSwiper.slides, target);
    if (index < 0) return;

    mainSwiper.slideTo(index, 0);
    if (subListEl && subListEl.swiper) {
      subListEl.swiper.slideTo(index, 0);
    }
  }

  // Thumbs strip is pointless with a single visible thumb — hide its wrapper.
  function toggleThumbsWrapper(wrapper, subItems) {
    var outer = wrapper.querySelector(".product__media-sublist-outer");
    if (!outer) return;

    var visible = 0;
    subItems.forEach(function (item) {
      if (!item.classList.contains(SUB_HIDDEN_CLASS)) visible++;
    });
    outer.style.display = visible <= 1 ? "none" : "";
  }

  function applyToWrapper(wrapper, selectedGroup, variant, sectionId) {
    var mainItems = wrapper.querySelectorAll("[data-media-id]");
    var subItems = wrapper.querySelectorAll("[data-media-sub-id]");

    toggleItems(mainItems, selectedGroup, MAIN_HIDDEN_CLASS);
    toggleItems(subItems, selectedGroup, SUB_HIDDEN_CLASS);

    var mainListEl = wrapper.querySelector(".js-media-list");
    var subListEl = wrapper.querySelector(".js-media-sublist");

    // Refresh thumbs first so the main slider's thumbs binding is in sync.
    refreshSwiper(subListEl);
    refreshSwiper(mainListEl);

    slideToSelected(mainListEl, subListEl, variant, sectionId, selectedGroup);
    toggleThumbsWrapper(wrapper, subItems);
  }

  window.updateVariantMediaDisplay = function (sectionId, variant) {
    if (sectionId == null) return;

    var root = findRoot(sectionId, document);
    if (!root) return; // grouping off, or product has no color option

    var groupOptionPosition = parseInt(
      root.getAttribute("data-group-option-position") || "0",
      10
    );
    if (!groupOptionPosition) return;

    var selectedGroup = "";
    if (variant && Array.isArray(variant.options)) {
      selectedGroup = handleize(variant.options[groupOptionPosition - 1]);
    }

    var scope = resolveScope(root);
    var wrappers = scope.querySelectorAll(".product__media-wrapper");
    wrappers.forEach(function (wrapper) {
      applyToWrapper(wrapper, selectedGroup, variant, sectionId);
    });
  };

  // Read the selected-variant JSON already present on the page for a gallery.
  function readSelectedVariant(root) {
    var scope = resolveScope(root);
    var el = scope.querySelector("[data-selected-variant]");
    if (!el) return null;
    try {
      var raw = (el.textContent || el.innerHTML || "").trim();
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function initAll(root) {
    var scope = root || document;
    var roots = scope.querySelectorAll(
      '[data-alt-media-grouping="true"][data-group-option-position]'
    );
    roots.forEach(function (rootEl) {
      var section = (rootEl.getAttribute("data-section") || "").replace(
        "product-media-",
        ""
      );
      if (!section) return;
      var variant = readSelectedVariant(rootEl);
      window.updateVariantMediaDisplay(section, variant);
    });
  }

  function boot() {
    // Sliders are initialised by update-slider.js (deferred, runs before this
    // point) — a rAF guards against any lingering layout work.
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(function () {
        initAll(document);
      });
    } else {
      initAll(document);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event && event.target ? event.target : document);
  });
})();
